import { mapValues, compact, uniq } from 'lodash-es';
import type { Airflow } from '@aviata/platform-types';
import type { PassengerKey, PassengersData } from '@aviata/booking-data';
import type { SeatsUtils } from '@aviata/seatmap-fns';
import type { flightEntity } from 'freedom-shared';

import { serializeSeatmap, type SerializedSeatmap } from './serialize-seatmap';
import { seatColors } from '../config';
import type {
  SelectedSeatsState,
  SeatMapLegend,
  PassengersState,
} from '../types';

export type {
  SerializedSeatmap,
  SerializedRow,
  SerializedSeat,
  SerializedColumn,
} from './serialize-seatmap';

type HasSegmentSeatsParams = {
  seatMapState: PassengersState;
  passengersKeys: PassengerKey[];
};

function generateRandomPastelColor(): string {
  // Generate random values for the R, G, and B components to create a pastel color.
  const r = Math.floor(Math.random() * 128) + 128; // Red component
  const g = Math.floor(Math.random() * 128) + 128; // Green component
  const b = Math.floor(Math.random() * 128) + 128; // Blue component

  // Convert the RGB values to hexadecimal and format them as a string.
  const hexColor = `#${r.toString(16)}${g.toString(16)}${b.toString(16)}`;

  return hexColor.toUpperCase(); // Convert the color to uppercase for consistency
}

function getSeatMapUniqPrices(seatmap: SerializedSeatmap) {
  const availableSeatsPrice = compact(
    seatmap.rows.flatMap(row =>
      row.seats.map(seat =>
        seat.exists && seat.available ? Number(seat.price?.amount) : null,
      ),
    ),
  );
  return uniq(
    availableSeatsPrice.sort((a, b) => b - a).map(price => String(price)),
  );
}

function getSeatmapLegend(seatmap: SerializedSeatmap) {
  const uniqPrices = getSeatMapUniqPrices(seatmap);
  return uniqPrices.reduce((acc, price, i) => {
    acc[price] = {
      base: seatColors[i]?.[0] ?? generateRandomPastelColor(),
      selected: seatColors[i]?.[1] ?? generateRandomPastelColor(),
    };
    return acc;
  }, {} as SeatMapLegend);
}

function getSeatmapInitialState(props: {
  segmentIds: number[];
  passengers: PassengersData;
}) {
  const { segmentIds, passengers } = props;

  return segmentIds.map(() => {
    return mapValues(passengers, () => {
      return {
        seatKey: '',
        seatPrice: 0,
        coordinates: {
          row: 0,
          col: '',
        },
      };
    });
  }) as SelectedSeatsState;
}

function preparePayload(state: SelectedSeatsState) {
  return state.reduce((acc, segment, segmentId) => {
    const seats = Object.values(segment);

    for (const passengerId in seats) {
      const seat = seats[passengerId];

      if (seat.seatKey) {
        const key = `${segmentId}:${seat.coordinates.row}:${seat.coordinates.col}`;

        acc[key] = {
          passengerId: String(passengerId),
          segmentId: String(segmentId),
          coordinates: seat.coordinates,
          price: String(seat.seatPrice),
        };
      }
    }

    return acc;
  }, {} as SeatsUtils.SelectedSeats);
}

function getDirectionText(segments: flightEntity.types.MappedSegment[]) {
  return `${segments[0].dep.city} — ${segments[segments.length - 1].arr.city}`;
}

function getPassengerType(key: string): Airflow.PassengerType {
  return key.slice(0, 3) as Airflow.PassengerType;
}

function isInfant(passengerKey: PassengerKey) {
  return getPassengerType(passengerKey) === 'INF';
}

function hasPassengerSeat(
  passengersState: PassengersState,
  passengerKey: PassengerKey,
) {
  return Boolean(passengersState[passengerKey].seatKey);
}

// return true if at least one passenger in segment has selected seat
function hasSegmentSeats({
  passengersKeys,
  seatMapState,
}: HasSegmentSeatsParams) {
  return passengersKeys.some(key => hasPassengerSeat(seatMapState, key));
}

function getSeatMapPrice(
  seatMapState: SelectedSeatsState,
  passengersKeys: PassengerKey[],
) {
  return seatMapState.reduce((acc, state) => {
    const priceOfSegment = passengersKeys.reduce((acc, key) => {
      acc += state[key].seatPrice;
      return acc;
    }, 0);
    acc += priceOfSegment;
    return acc;
  }, 0);
}

export const seatMapLib = {
  preparePayload,
  mapSeatMap: serializeSeatmap,
  getSeatMapLegend: getSeatmapLegend,
  getSeatMapInitialState: getSeatmapInitialState,
  getDirectionText,
  isInfant,
  hasPassengerSeat,
  hasSegmentSeats,
  getSeatMapPrice,
};
